import { useEffect, useState } from 'react';
import { DollarSign, TrendingUp, Users } from 'lucide-react';
import CommissionCalculator from '../components/CommissionCalculator';

interface Commission {
  seller_id: string;
  seller_name: string;
  seller_email: string;
  total_sales: number;
  total_revenue: number;
  total_commission: number;
  sales_count: number;
}

export default function Comissoes() {
  const [report, setReport] = useState<{ sellers: Commission[]; total_revenue: number; total_commissions: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    loadReport();
  }, [filter]);

  const loadReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filter.start_date) params.append('start_date', filter.start_date);
      if (filter.end_date) params.append('end_date', filter.end_date);

      const res = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/commissions/report?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setReport(data.data);
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando comissões...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Comissões</h1>
        <p className="text-gray-600">Acompanhe as comissões dos vendedores</p>
      </div>

      {/* Cards de Resumo */}
      {report && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Receita Total</p>
                <p className="text-2xl font-bold text-gray-900">${report.total_revenue.toFixed(2)}</p>
              </div>
              <DollarSign className="h-12 w-12 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Comissões Totais</p>
                <p className="text-2xl font-bold text-gray-900">${report.total_commissions.toFixed(2)}</p>
              </div>
              <TrendingUp className="h-12 w-12 text-purple-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Vendedores Ativos</p>
                <p className="text-2xl font-bold text-gray-900">{report.sellers.length}</p>
              </div>
              <Users className="h-12 w-12 text-blue-500" />
            </div>
          </div>
        </div>
      )}

      {/* Calculadora de Comissão */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Calculadora de Comissão</h2>
        <CommissionCalculator />
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Período</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Início</label>
            <input
              type="date"
              value={filter.start_date}
              onChange={(e) => setFilter({ ...filter, start_date: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Fim</label>
            <input
              type="date"
              value={filter.end_date}
              onChange={(e) => setFilter({ ...filter, end_date: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilter({ start_date: '', end_date: '' })}
              className="w-full bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabela de Comissões por Vendedor */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Comissões por Vendedor</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendedor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendas</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receita Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Comissão Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">% Comissão</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {!report || report.sellers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    Nenhuma comissão encontrada
                  </td>
                </tr>
              ) : (
                report.sellers.map((seller) => (
                  <tr key={seller.seller_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{seller.seller_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{seller.seller_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {seller.sales_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ${seller.total_revenue.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                      ${seller.total_commission.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {seller.total_revenue > 0 
                        ? ((seller.total_commission / seller.total_revenue) * 100).toFixed(2)
                        : '0.00'}%
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {report && report.sellers.length > 0 && (
              <tfoot className="bg-gray-50 font-bold">
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-sm text-gray-900">TOTAL</td>
                  <td className="px-6 py-4 text-sm text-gray-900">${report.total_revenue.toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm text-green-600">${report.total_commissions.toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {report.total_revenue > 0 
                      ? ((report.total_commissions / report.total_revenue) * 100).toFixed(2)
                      : '0.00'}%
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
